import * as React from 'react';
import type { UseFormSetValue } from 'react-hook-form';
import type {
	IEventRegistrationSurvey,
	IEventSurveyResponse,
	IPublicFileAttachment,
	IRecipient,
} from '../../../../../../../models/Api';
import { getEventRecipientFirstAndLastName } from '../../utils';

interface ITicketHolder {
	id?: string; // Unique ID from backend or generated UUID
	ticketId: string;
	ticketName: string;
	firstName: string;
	lastName: string;
	email: string;
	phone: string;
	isPaid?: boolean; // Whether this ticket has been paid for
	ticketItemIndex?: number | null; // Which physical ticket (table) this seat belongs to; undefined for single-seat tickets
}

interface IFormValues {
	isAttending: boolean | null;
	isPaid: boolean;
	bringingGuest?: boolean;
	email?: string;
	firstName?: string;
	lastName?: string;
	phone?: string;
	guests: IRecipient[];
	customProperties: Record<string, string | IPublicFileAttachment[]>;
	ticketSelections?: Record<string, number>;
	ticketHolders?: ITicketHolder[];
}

interface UseEventSurveyFormInitializationProps {
	survey?: IEventRegistrationSurvey;
	surveyResponse?: IEventSurveyResponse;
	urlParams: { token?: string };
	isAttending: boolean | null;
	processingPayment: React.MutableRefObject<boolean>;
	pendingAttendanceSelection: React.MutableRefObject<boolean | null>;
	setValue: UseFormSetValue<IFormValues>;
	getValues: <K extends keyof IFormValues>(name?: K) => K extends keyof IFormValues ? IFormValues[K] : IFormValues;
	setLoading: (loading: boolean) => void;
	DEFAULT_VALUES: IFormValues;
	setPrimaryTicketHolderId: (id: string | null) => void;
}

export const useEventSurveyFormInitialization = ({
	survey,
	surveyResponse,
	urlParams,
	isAttending,
	processingPayment,
	pendingAttendanceSelection,
	setValue,
	getValues,
	setLoading,
	DEFAULT_VALUES,
	setPrimaryTicketHolderId,
}: UseEventSurveyFormInitializationProps) => {
	React.useEffect(() => {
		if (!urlParams.token || urlParams.token.toLocaleLowerCase() === 'unavailable') {
			setLoading(false);
			return;
		}

		const getValueFromRegisteredAndUnregisteredGuests = () => {
			const guestsRegisteredByName = surveyResponse?.guests ?? [];
			const anonymousGuestCount = surveyResponse?.anonymousGuestCount ?? 0;
			if (guestsRegisteredByName.length || surveyResponse?.anonymousGuestCount) {
				const registeredGuests = [...guestsRegisteredByName];
				// For anonymous guests, create placeholder entries with isPaid: false
				for (let i = 0; i < anonymousGuestCount; i++) {
					registeredGuests.push({ firstName: '', lastName: '', isPaid: false });
				}
				return registeredGuests;
			}
			return DEFAULT_VALUES.guests;
		};

		if (surveyResponse) {
			setLoading(true);
			const { firstName, lastName } = getEventRecipientFirstAndLastName(surveyResponse.attendee);

			// During payment processing, preserve the user's intended attendance selection
			// The backend may reset isAttending to undefined until payment is complete
			let isAttendeeAttending;
			if (processingPayment.current && pendingAttendanceSelection.current !== null) {
				// Use the pending selection during payment processing
				isAttendeeAttending = pendingAttendanceSelection.current;
			} else {
				// Normal flow: use backend value or fallback to prop
				isAttendeeAttending = surveyResponse?.isAttending !== undefined ? surveyResponse.isAttending : isAttending;
			}

			setValue('isAttending', isAttendeeAttending);
			setValue('email', surveyResponse?.attendee?.email);
			setValue('guests', getValueFromRegisteredAndUnregisteredGuests());
			setValue('firstName', firstName);
			setValue('lastName', lastName);
			setValue('phone', surveyResponse?.attendee?.phone);
			setValue('bringingGuest', Boolean(surveyResponse?.guests?.length || surveyResponse?.anonymousGuestCount));

			/**
			 * Load ticket selections and ticket holders for tiered ticketing
			 *
			 * BUG FIX: Preserve existing ticket holder array order when updating after submission
			 *
			 * This effect runs whenever surveyResponse changes (including after submission).
			 * Previously, it always rebuilt the array using Object.entries(), which has unpredictable
			 * order, causing tickets to be reordered and attendee assigned to wrong position.
			 *
			 * NEW APPROACH:
			 * - If form already has ticket holders (after submission): UPDATE existing array with backend data
			 * - If form is empty (initial load): BUILD fresh array from backend response
			 *
			 * This preserves the user's original ticket selection order throughout the flow.
			 */
			if (
				survey?.isPaidEvent &&
				survey?.attendeeOptions?.eventTickets &&
				survey.attendeeOptions.eventTickets.length > 0
			) {
				const ticketSelections: Record<string, number> = {};

				// Count physical tickets (units) from the backend response, NOT seat records: seats
				// sharing a (ticket, ticketItemIndex) are one ticket; records with no index count
				// individually. Mirrors getPaidTicketQuantities so ticketSelections stays ticket-unit
				// counts — both pricing and holder provisioning (quantity × attendeeCountPerItem) rely
				// on that. (Counting records here made a couple's ticket read as 2 units → phantom holders.)
				const backendRecipients = [surveyResponse.attendee, ...(surveyResponse.guests ?? [])];
				const seenIndexesByTicket: Record<string, Set<number>> = {};
				const nullIndexCountByTicket: Record<string, number> = {};
				backendRecipients.forEach(recipient => {
					const ticketId = recipient?.ticket?.id;
					if (!ticketId) return;
					const index = recipient?.ticketItemIndex;
					if (index == null) {
						nullIndexCountByTicket[ticketId] = (nullIndexCountByTicket[ticketId] || 0) + 1;
					} else {
						(seenIndexesByTicket[ticketId] ??= new Set<number>()).add(index);
					}
				});
				new Set([...Object.keys(seenIndexesByTicket), ...Object.keys(nullIndexCountByTicket)]).forEach(ticketId => {
					ticketSelections[ticketId] =
						(seenIndexesByTicket[ticketId]?.size ?? 0) + (nullIndexCountByTicket[ticketId] ?? 0);
				});

				// Holder rows are per person (record), so compare the existing array against the
				// record count — not the ticket-unit count above.
				const currentHolders: ITicketHolder[] = (getValues('ticketHolders') as ITicketHolder[] | undefined) || [];
				const totalBackendRecords = backendRecipients.filter(r => r?.ticket?.id).length;
				const shouldUpdate = currentHolders.length === totalBackendRecords && currentHolders.length > 0;

				if (shouldUpdate) {
					/**
					 * UPDATE MODE: Form already has ticket holders (after submission)
					 * Preserve existing array structure/order, update all fields from backend
					 */
					let primaryHolderId: string | null = null;

					// Update all fields from backend response
					const updatedHolders = [...currentHolders];

					// Mark attendee as primary and update all fields
					if (surveyResponse.attendee?.ticket?.id && surveyResponse.attendee?.id) {
						const attendeeId = surveyResponse.attendee.id;
						const attendeeIndex = updatedHolders.findIndex(h => h.id === attendeeId);

						if (attendeeIndex !== -1) {
							updatedHolders[attendeeIndex] = {
								...updatedHolders[attendeeIndex],
								firstName: surveyResponse.attendee.firstName || updatedHolders[attendeeIndex].firstName,
								lastName: surveyResponse.attendee.lastName || updatedHolders[attendeeIndex].lastName,
								email: surveyResponse.attendee.email || updatedHolders[attendeeIndex].email,
								phone: surveyResponse.attendee.phone || updatedHolders[attendeeIndex].phone,
								isPaid: surveyResponse.attendee.isPaid || false,
								ticketItemIndex: surveyResponse.attendee.ticketItemIndex ?? null,
							};
							primaryHolderId = attendeeId;
						}
					}

					// Update all fields for guests
					surveyResponse.guests?.forEach(guest => {
						if (guest.id) {
							const guestIndex = updatedHolders.findIndex(h => h.id === guest.id);
							if (guestIndex !== -1) {
								updatedHolders[guestIndex] = {
									...updatedHolders[guestIndex],
									firstName: guest.firstName || updatedHolders[guestIndex].firstName,
									lastName: guest.lastName || updatedHolders[guestIndex].lastName,
									email: guest.email || updatedHolders[guestIndex].email,
									phone: guest.phone || updatedHolders[guestIndex].phone,
									isPaid: guest.isPaid || false,
									ticketItemIndex: guest.ticketItemIndex ?? null,
								};
							}
						}
					});

					// Set values (array structure unchanged, but all fields updated)
					setValue('ticketSelections', ticketSelections);
					setValue('ticketHolders', updatedHolders);
					if (primaryHolderId) {
						setPrimaryTicketHolderId(primaryHolderId);
					}
				} else {
					/**
					 * BUILD MODE: Initial load or structure changed
					 * Build fresh ticket holders array from backend response with IDs
					 */
					const ticketHolders: ITicketHolder[] = [];
					let primaryHolderId: string | null = null;

					// Step 1: Add attendee first (so they're primary)
					if (surveyResponse.attendee?.ticket?.id) {
						ticketHolders.push({
							id: surveyResponse.attendee.id || crypto.randomUUID(),
							ticketId: surveyResponse.attendee.ticket.id,
							ticketName: surveyResponse.attendee.ticket.name || '',
							firstName: surveyResponse.attendee.firstName || '',
							lastName: surveyResponse.attendee.lastName || '',
							email: surveyResponse.attendee.email || '',
							phone: surveyResponse.attendee.phone || '',
							isPaid: surveyResponse.attendee.isPaid || false,
							ticketItemIndex: surveyResponse.attendee.ticketItemIndex ?? null,
						});
						primaryHolderId = surveyResponse.attendee.id || ticketHolders[0].id || null;
					}

					// Step 2: Add guests
					surveyResponse.guests?.forEach(guest => {
						if (guest.ticket?.id) {
							ticketHolders.push({
								id: guest.id || crypto.randomUUID(),
								ticketId: guest.ticket.id,
								ticketName: guest.ticket.name || '',
								firstName: guest.firstName || '',
								lastName: guest.lastName || '',
								email: guest.email || '',
								phone: guest.phone || '',
								isPaid: guest.isPaid || false,
								ticketItemIndex: guest.ticketItemIndex ?? null,
							});
						}
					});

					// Set values with freshly built array
					setValue('ticketSelections', ticketSelections);
					setValue('ticketHolders', ticketHolders);
					if (primaryHolderId) {
						setPrimaryTicketHolderId(primaryHolderId);
					}
				}
			}

			const customProperties = surveyResponse?.customProperties ?? {};
			if (customProperties) {
				Object.entries(customProperties).forEach(([key, value]) => {
					setValue(`customProperties.${key}`, value);
				});
			}
			setLoading(false);
			return;
		}
		/**
		 * No data found for a user from the token;
		 * continue as an anonymous user
		 */
		setLoading(false);
	}, [
		surveyResponse,
		setValue,
		getValues,
		urlParams.token,
		survey?.isPaidEvent,
		survey?.attendeeOptions?.eventTickets,
		setLoading,
		isAttending,
		processingPayment,
		pendingAttendanceSelection,
		DEFAULT_VALUES.guests,
		setPrimaryTicketHolderId,
	]);
};
